import React, { useMemo, useState } from 'react';

import Icon from 'views/site/Icon.js';

import Button from 'views/site/Button.js';
import Catalog from 'files/Catalog.js';
import NetworkCoverage from 'files/NetworkCoverage.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * CoverageQuiz
 *
 * The in-home walkthrough, on the web. The storefront's quiz asked about
 * plumbing and answered with three priced tiers; this one walks the house the
 * way an advisor does — bedrooms first, then levels, living areas, the rooms a
 * smoke alarm cannot watch, and finally water — and answers with the devices
 * that home needs and the reason behind each one.
 *
 * There is deliberately no good/better/best. Tiers are a device for making a
 * price feel like a choice, and there is no price here. Where a manual or
 * NFPA 72 requires something it is marked required; everything else is marked
 * recommended, and the visitor can change any quantity afterwards.
 */

// Every chip is this tall, selected or not. See the note in the chip styles.
const CHIP_HEIGHT = 46;

const CoverageQuiz = ({ dealership, initialAnswers, onAddToQuote }) => {

    const mobile = Utils.isMobile();
    const mode = Catalog.getMode(dealership);
    const steps = useMemo(() => NetworkCoverage.steps(mode), [mode]);

    // Seeded from a walkthrough that was already completed and saved with the
    // quote, so the answers survive a page reload as well as a closed sheet.
    let seeded = initialAnswers && Object.keys(initialAnswers).length > 0;

    const [answers, setAnswers] = useState(initialAnswers || {});
    const [index, setIndex] = useState(0);
    const [complete, setComplete] = useState(seeded === true);

    const step = steps[index];
    const plan = useMemo(() => NetworkCoverage.plan(answers, mode), [answers, mode]);

    const setAnswer = (id, value) => setAnswers(current => ({ ...current, [id]: value }));

    const onNext = () => {
        if(index < steps.length - 1) {
            setIndex(index + 1);
            return;
        }
        setComplete(true);
    }

    const onBack = () => {
        if(complete === true) {
            setComplete(false);
            return;
        }
        setIndex(Math.max(0, index - 1));
    }

    const onStartOver = () => {
        setAnswers({});
        setIndex(0);
        setComplete(false);
    }

    // Every step is skippable — a home without a garage should not be stuck.
    const isAnswered = () => {
        if(!step) {
            return false;
        }
        if(step.kind === 'count') {
            return answers[step.id] !== undefined;
        }
        return true;
    }

    // ---- controls -----------------------------------------------------

    const getChip = ({ active, icon, key, label, onClick }) => (
        <div
        key={key}
        onClick={onClick}
        style={{
            alignItems: 'center',
            backgroundColor: active ? Theme.colors.primaryFill() : Theme.colors.white,
            border: `1px solid ${active ? Theme.colors.primaryFill() : Theme.colors.border()}`,
            borderRadius: 999,
            boxSizing: 'border-box',
            color: active ? Theme.colors.onPrimary() : Theme.colors.text(),
            cursor: 'pointer',
            display: 'inline-flex',
            fontFamily: Theme.fonts.body,
            fontSize: 15,
            fontWeight: 500,
            gap: 9,
            // fixed height, horizontal padding only: selecting a chip swaps its
            // contents for a stepper, and any vertical padding meant the chip
            // changed size the moment you tapped it
            height: CHIP_HEIGHT,
            padding: '0 20px',
            transition: 'all 0.15s ease',
            userSelect: 'none'
        }}>
            {icon && <Icon name={icon} size={15} style={{ opacity: active ? 1 : 0.55 }} />}
            <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
        </div>
    );

    /*
     * The quantity step is a table, not chips.
     *
     * Ten items each carrying their own count is a different job from picking
     * one number or toggling a set. As wrapped chips the labels landed at
     * unpredictable positions, the steppers moved every time a count changed
     * width, and scanning "what have I already set" meant reading the whole
     * block. One row per item puts every label at the same left edge and every
     * stepper at the same right edge, so the eye runs straight down both.
     *
     * The stepper is always present, including at zero, with minus disabled.
     * Revealing it on first tap would mean the row changes size as you use it,
     * which is the bug this step just had.
     */
    const getQuantityRow = (option, index) => {

        let quantities = answers[step.id] || {};
        let quantity = quantities[option.id] || 0;
        let active = quantity > 0;

        const setQuantity = value => setAnswer(step.id, {
            ...quantities,
            [option.id]: Math.max(0, value)
        });

        const stepper = (icon, onClick, label, disabled) => (
            <button
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            style={{
                alignItems: 'center',
                backgroundColor: disabled ? 'transparent' : Theme.tint(0.1),
                border: `1px solid ${disabled ? Theme.colors.border() : 'transparent'}`,
                borderRadius: 999,
                color: disabled ? Theme.colors.lighterGrey : Theme.colors.primary(),
                cursor: disabled ? 'default' : 'pointer',
                display: 'inline-flex',
                flexShrink: 0,
                height: 32,
                justifyContent: 'center',
                padding: 0,
                transition: 'background-color 0.15s ease',
                width: 32
            }}>
                <Icon name={icon} size={13} />
            </button>
        );

        return (
            <div
            key={option.id}
            style={{
                alignItems: 'center',
                backgroundColor: active ? Theme.tint(0.05) : Theme.colors.white,
                borderTop: index === 0 ? 'none' : `1px solid ${Theme.colors.divider()}`,
                display: 'flex',
                gap: 16,
                justifyContent: 'space-between',
                minHeight: 60,
                padding: mobile ? '10px 14px' : '10px 18px',
                transition: 'background-color 0.15s ease'
            }}>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 12,
                    minWidth: 0
                }}>
                    {option.icon && (
                        <Icon
                        color={active ? Theme.colors.primary() : Theme.colors.lightGrey}
                        name={option.icon}
                        size={18} />
                    )}
                    <span style={Theme.textStyles.body({
                        color: Theme.colors.text(),
                        fontSize: 15,
                        fontWeight: active ? 600 : 400
                    })}>{option.label}</span>
                </div>

                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexShrink: 0,
                    gap: 6
                }}>
                    {stepper('dash-lg', setQuantity.bind(this, quantity - 1), `One fewer ${option.label}`, active === false)}
                    <span style={{
                        ...Theme.textStyles.body({
                            color: active ? Theme.colors.text() : Theme.colors.lightGrey,
                            fontSize: 16,
                            fontWeight: 700
                        }),
                        fontVariantNumeric: 'tabular-nums',
                        textAlign: 'center',
                        width: 28
                    }}>{quantity}</span>
                    {stepper('plus-lg', setQuantity.bind(this, quantity + 1), `One more ${option.label}`, false)}
                </div>
            </div>
        );
    };

    const getStepBody = () => {

        if(step.kind === 'count') {
            return step.options.map(option => getChip({
                active: answers[step.id] === option,
                key: option,
                label: option === 0 ? 'None' : `${option}`,
                onClick: setAnswer.bind(this, step.id, option)
            }));
        }

        if(step.kind === 'multi') {
            let selected = answers[step.id] || [];
            return step.options.map(option => getChip({
                active: selected.includes(option.id),
                icon: option.icon,
                key: option.id,
                label: option.label,
                onClick: () => setAnswer(step.id, selected.includes(option.id)
                    ? selected.filter(id => id !== option.id)
                    : selected.concat([option.id]))
            }));
        }

        return (
            <div style={{
                border: `1px solid ${Theme.colors.border()}`,
                borderRadius: 16,
                overflow: 'hidden',
                width: '100%'
            }}>
                {step.options.map(getQuantityRow)}
            </div>
        );
    };

    // ---- result -------------------------------------------------------

    const getResult = () => {

        if(plan.devices.length === 0) {
            return (
                <div style={{ textAlign: 'center' }}>
                    <span style={{
                        ...Theme.textStyles.body(),
                        display: 'block',
                        marginBottom: 24
                    }}>{'You did not tell us anything about the home, so there is nothing to recommend yet.'}</span>
                    <Button label={'Start over'} onClick={onStartOver} type={'outline'} />
                </div>
            )
        }

        let required = plan.devices.filter(device => device.requirement === 'required');

        return (
            <div>
                <div style={{ marginBottom: mobile ? 28 : 36, textAlign: 'center' }}>
                    <span style={{
                        ...Theme.textStyles.label(),
                        display: 'block',
                        marginBottom: 12
                    }}>{'Your home'}</span>
                    <span style={{
                        ...Theme.textStyles.heading({ fontSize: mobile ? 24 : 30 }),
                        display: 'block',
                        marginBottom: 12
                    }}>{`${plan.total} device${plan.total === 1 ? '' : 's'} for full coverage`}</span>
                    <span style={{
                        ...Theme.textStyles.body(),
                        display: 'block',
                        margin: '0 auto',
                        maxWidth: '54ch'
                    }}>{required.length > 0
                        ? 'The devices marked required are what code and the manufacturer call for in a home like yours. The rest is what we would put in our own.'
                        : 'Here is what we would put in a home like yours.'}</span>
                </div>

                <div style={{
                    border: `1px solid ${Theme.colors.border()}`,
                    borderRadius: 16,
                    overflow: 'hidden'
                }}>
                    {plan.devices.map((device, deviceIndex) => {

                        let product = Catalog.getByHandle(device.handle, mode);
                        if(!product) {
                            return null;
                        }

                        return (
                            <div
                            key={device.handle}
                            style={{
                                borderTop: deviceIndex === 0 ? 'none' : `1px solid ${Theme.colors.divider()}`,
                                display: 'flex',
                                gap: mobile ? 14 : 18,
                                padding: mobile ? '18px 16px' : '22px 24px'
                            }}>
                                <div style={{
                                    alignItems: 'center',
                                    backgroundColor: Theme.colors.offWhite,
                                    borderRadius: 12,
                                    display: 'flex',
                                    flexShrink: 0,
                                    height: 56,
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    width: 56
                                }}>
                                    {product.images?.length > 0 ? (
                                        <img
                                        alt={product.title}
                                        src={product.images[0].src}
                                        style={{ height: '100%', objectFit: 'cover', width: '100%' }} />
                                    ) : (
                                        <Icon name={product.icon} size={20} color={Theme.colors.lightGrey} />
                                    )}
                                </div>

                                <div style={{ flexGrow: 1, minWidth: 0 }}>
                                    <div style={{
                                        alignItems: 'baseline',
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 10,
                                        marginBottom: 6
                                    }}>
                                        <span style={Theme.textStyles.subHeading({ fontSize: 17 })}>
                                            {`${device.quantity} × ${product.title}`}
                                        </span>
                                        <span style={{
                                            backgroundColor: device.requirement === 'required'
                                                ? Theme.tint(0.12)
                                                : Theme.colors.offWhite,
                                            borderRadius: 999,
                                            color: device.requirement === 'required' ? Theme.colors.primary() : Theme.colors.subText(),
                                            fontFamily: Theme.fonts.body,
                                            fontSize: 11,
                                            fontWeight: 700,
                                            letterSpacing: '0.06em',
                                            padding: '3px 9px',
                                            textTransform: 'uppercase'
                                        }}>{device.requirement}</span>
                                    </div>
                                    {device.reasons.map((reason, reasonIndex) => (
                                        <div
                                        key={reasonIndex}
                                        style={{
                                            display: 'flex',
                                            gap: 10,
                                            marginTop: reasonIndex === 0 ? 0 : 8
                                        }}>
                                            <span style={{
                                                backgroundColor: Theme.colors.primary(),
                                                borderRadius: '50%',
                                                flexShrink: 0,
                                                height: 5,
                                                marginTop: 8,
                                                opacity: 0.55,
                                                width: 5
                                            }} />
                                            <span style={Theme.textStyles.body({ fontSize: 14 })}>{reason}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )
                    })}
                </div>

                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: mobile ? 'column' : 'row',
                    gap: 16,
                    justifyContent: 'center',
                    marginTop: 32
                }}>
                    <Button
                    icon={'plus-lg'}
                    label={'Add All To My Network'}
                    onClick={() => onAddToQuote && onAddToQuote(plan, answers)}
                    style={{ fontSize: 16, padding: '15px 36px', width: mobile ? '100%' : 'auto' }}
                    type={'primary'} />
                    <span
                    onClick={onStartOver}
                    style={{
                        color: Theme.colors.primary(),
                        cursor: 'pointer',
                        fontFamily: Theme.fonts.body,
                        fontSize: 15,
                        fontWeight: 500
                    }}>{'Start over'}</span>
                </div>

                <span style={{
                    ...Theme.textStyles.caption({ color: Theme.colors.lightGrey, textAlign: 'center' }),
                    display: 'block',
                    marginTop: 20
                }}>{`Nothing here is priced or ordered. ${dealership?.name || 'Your local dealer'} confirms the layout during the visit — you can change any quantity first.`}</span>
            </div>
        )
    }

    // ---- shell --------------------------------------------------------

    if(complete === true) {
        return (
            <div>
                <span
                onClick={onBack}
                style={{
                    color: Theme.colors.primary(),
                    cursor: 'pointer',
                    display: 'inline-block',
                    fontFamily: Theme.fonts.body,
                    fontSize: 15,
                    fontWeight: 500,
                    marginBottom: 24
                }}>{'‹ Back to the walkthrough'}</span>
                {getResult()}
            </div>
        )
    }

    return (
        <div>
            {/* Progress. The steps are a route through a house, so the reading
                is "where am I", not "how much is left". */}
            <div style={{
                display: 'flex',
                gap: 6,
                marginBottom: 26
            }}>
                {steps.map((item, itemIndex) => (
                    <div
                    key={item.id}
                    style={{
                        backgroundColor: itemIndex <= index ? Theme.colors.primaryFill() : Theme.colors.lighterGrey,
                        borderRadius: 2,
                        flexGrow: 1,
                        height: 3,
                        transition: 'background-color 0.3s ease'
                    }} />
                ))}
            </div>

            <div style={{
                alignItems: 'center',
                display: 'flex',
                gap: 12,
                marginBottom: 10
            }}>
                <Icon name={step.icon} size={15} color={Theme.colors.primary()} />
                <span style={Theme.textStyles.label()}>{`Step ${index + 1} of ${steps.length} · ${step.title}`}</span>
            </div>

            <span style={{
                ...Theme.textStyles.heading({ fontSize: mobile ? 24 : 30 }),
                display: 'block',
                marginBottom: 12
            }}>{step.question}</span>

            <span style={{
                ...Theme.textStyles.body(),
                display: 'block',
                marginBottom: 26,
                maxWidth: '58ch'
            }}>{step.help}</span>

            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 12,
                ...step.kind === 'quantity' && { display: 'block' }
            }}>
                {getStepBody()}
            </div>

            <div style={{
                alignItems: 'center',
                display: 'flex',
                gap: 20,
                marginTop: 36
            }}>
                {index > 0 && (
                    <span
                    onClick={onBack}
                    style={{
                        color: Theme.colors.subText(),
                        cursor: 'pointer',
                        fontFamily: Theme.fonts.body,
                        fontSize: 15,
                        fontWeight: 500
                    }}>{'‹ Back'}</span>
                )}
                <Button
                disabled={isAnswered() === false}
                label={index === steps.length - 1 ? 'See what my home needs ›' : 'Next ›'}
                onClick={onNext}
                style={{ padding: '14px 32px' }}
                type={'primary'} />
            </div>
        </div>
    )
}

export default CoverageQuiz;
